import React, { useState, useEffect } from 'react';
import {
  X,
  Server,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  FileText,
  FolderOpen,
  Folder,
  Copy,
  Check,
} from 'lucide-react';
import { testSapSftpListFilesAPI } from '../../../api/sapSync';
import type { SapTestSftpResponse, SapTestSftpFile } from '../../../types/sapSync';

interface TestSftpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TestSftpModal: React.FC<TestSftpModalProps> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SapTestSftpResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedFile, setCopiedFile] = useState<string | null>(null);

  const fetchSftpFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await testSapSftpListFilesAPI();
      setData(res);
    } catch (err: any) {
      console.error('SFTP test error:', err);
      setError(err.message || 'Failed to connect to SFTP server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchSftpFiles();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = (fileName: string) => {
    navigator.clipboard.writeText(fileName);
    setCopiedFile(fileName);
    setTimeout(() => setCopiedFile(null), 2000);
  };

  const formatFileSize = (bytes?: number, isDir?: boolean) => {
    if (isDir) return '-';
    if (bytes === undefined || bytes === null || bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatModifyTime = (modifyTime?: string | number) => {
    if (!modifyTime) return '-';
    try {
      const ms = typeof modifyTime === 'number' ? modifyTime : parseInt(String(modifyTime), 10);
      return new Date(ms).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return String(modifyTime);
    }
  };

  const protocol = data?.protocol || data?.data?.protocol || 'SFTP / FTP';
  const directory = data?.directory || data?.data?.directory || '/';
  const files: SapTestSftpFile[] = (data?.files || data?.data?.files || []) as SapTestSftpFile[];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden animate-scaleUp">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/80">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center">
              <Server className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">SAP SFTP / FTP Connection Test</h3>
              <p className="text-xs text-slate-500">Live connection check & remote directory explorer</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchSftpFiles}
              disabled={loading}
              title="Refresh Connection"
              className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-200/60 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {/* Status banner */}
          {error ? (
            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-rose-800">Connection Failed</h4>
                <p className="text-xs text-rose-700 mt-0.5">{error}</p>
              </div>
            </div>
          ) : (
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-emerald-800">Server Connected Successfully</h4>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                    Online
                  </span>
                </div>
                <p className="text-xs text-emerald-700 mt-0.5">
                  Remote directory successfully scanned and verified.
                </p>
              </div>
            </div>
          )}

          {/* Connection Details */}
          {data && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200/70 text-xs">
              <div>
                <span className="text-slate-400 font-medium">Protocol</span>
                <p className="font-semibold text-slate-800 uppercase mt-0.5 font-mono">{protocol}</p>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Remote Directory</span>
                <p className="font-semibold text-slate-800 mt-0.5 font-mono truncate">{directory}</p>
              </div>
              <div>
                <span className="text-slate-400 font-medium">Items Found</span>
                <p className="font-semibold text-slate-800 mt-0.5 font-mono">{files.length} items</p>
              </div>
            </div>
          )}

          {/* Files List */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <FolderOpen className="w-4 h-4 text-blue-500" />
                Remote Directory Contents ({files.length})
              </h4>
            </div>

            {loading ? (
              <div className="py-12 flex flex-col items-center justify-center text-slate-400">
                <RefreshCw className="w-7 h-7 animate-spin text-blue-500 mb-2" />
                <p className="text-xs font-medium">Connecting to server and listing files...</p>
              </div>
            ) : files.length === 0 ? (
              <div className="py-8 text-center bg-slate-50 rounded-xl border border-slate-200/60 text-slate-500">
                <FileText className="w-8 h-8 mx-auto text-slate-300 mb-1" />
                <p className="text-xs font-semibold">No files currently in folder</p>
                <p className="text-[11px] text-slate-400">Directory is empty or already processed.</p>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-left text-xs border-collapse">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                    <tr>
                      <th className="py-2.5 px-3">Name</th>
                      <th className="py-2.5 px-3 w-20">Type</th>
                      <th className="py-2.5 px-3 w-24">Size</th>
                      <th className="py-2.5 px-3 w-44">Last Modified</th>
                      <th className="py-2.5 px-3 w-16 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {files.map((file, idx) => {
                      const isDirectory = file.type === 'd';

                      return (
                        <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                          <td className="py-2.5 px-3 font-mono text-slate-700 flex items-center gap-2">
                            {isDirectory ? (
                              <Folder className="w-4 h-4 text-amber-500 shrink-0" />
                            ) : (
                              <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                            )}
                            <span className="truncate max-w-sm font-semibold" title={file.name}>
                              {file.name}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-slate-500">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                                isDirectory
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {file.type}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-slate-600 font-mono">
                            {formatFileSize(file.size, isDirectory)}
                          </td>
                          <td className="py-2.5 px-3 text-slate-500">
                            {formatModifyTime(file.modifyTime)}
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <button
                              onClick={() => handleCopy(file.name)}
                              title="Copy name"
                              className="p-1 hover:bg-slate-200 rounded text-slate-500 hover:text-slate-800 transition-colors"
                            >
                              {copiedFile === file.name ? (
                                <Check className="w-3.5 h-3.5 text-emerald-600" />
                              ) : (
                                <Copy className="w-3.5 h-3.5" />
                              )}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3.5 border-t border-slate-100 bg-slate-50/50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold shadow-xs transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestSftpModal;
